import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, PasswordInput, Checkbox } from '@medikei/ui';
import { AuthLayout, AuthHeader, GoogleLoginButton } from '@/components/auth';
import { Captcha } from '@/components/common/Captcha';
import { authApi, AuthApiError, httpClient } from '@/api/http-client';
import {
  passwordValidationRules,
  getPasswordStrength,
} from '@/utils/validation';
import { getStoredPartnerHash } from '@/lib/deep-link';
import {
  CheckCircle,
  XCircle,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/utils/cn';

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
}

const patientRegisterSchema = z
  .object({
    name: z
      .string()
      .min(3, 'Nome completo deve ter pelo menos 3 caracteres')
      .max(100, 'Nome deve ter no máximo 100 caracteres'),
    email: z.string().min(1, 'Informe seu endereço de e-mail').email('E-mail em formato inválido (ex.: seu.nome@email.com)'),
    cpf: z
      .string()
      .min(1, 'CPF é obrigatório para emissão de prontuários e receitas')
      .refine((val) => /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(val) || val.replace(/\D/g, '').length === 11, 'CPF incompleto ou inválido. Digite os 11 números'),
    phone: z
      .string()
      .min(1, 'Telefone com DDD é obrigatório')
      .refine((val) => /^\(\d{2}\)\s?\d{4,5}-\d{4}$/.test(val) || val.replace(/\D/g, '').length >= 10, 'Telefone inválido. Formato esperado: (XX) 9XXXX-XXXX'),
    birthDate: z
      .string()
      .min(1, 'Data de nascimento é obrigatória')
      .refine((val) => /^\d{2}\/\d{2}\/\d{4}$/.test(val), 'Data de nascimento incompleta. Use o formato DD/MM/AAAA'),
    password: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres'),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
    lgpdConsent: z.boolean().refine((val) => val === true, 'Você deve concordar com a Política de Privacidade e o tratamento de dados (LGPD)'),
    termsConsent: z.boolean().refine((val) => val === true, 'Você deve aceitar os Termos de Uso da plataforma Medikei'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas digitadas não coincidem. Verifique e tente novamente',
    path: ['confirmPassword'],
  });

type PatientRegisterForm = z.infer<typeof patientRegisterSchema>;

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [apiError, setApiError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string>('');

  const urlParams = new URLSearchParams(location.search);
  const redirectParam = urlParams.get('redirect') || urlParams.get('returnUrl');
  const loginUrl = redirectParam
    ? `/login?redirect=${encodeURIComponent(redirectParam)}`
    : '/login';

  // Redireciona imediatamente se a URL indicar cadastro de médico
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const campaignCode = urlParams.get('campaignCode');
    const urlCode = urlParams.get('type');

    if (urlCode === 'doctor' || urlParams.get('role') === 'doctor') {
      navigate('/register/doctor', { replace: true });
      return;
    }

    const storedHash = getStoredPartnerHash();
    if (campaignCode) {
      setReferralCode(campaignCode);
    } else if (urlCode === 'referral' && storedHash) {
      setReferralCode(storedHash);
    } else if (storedHash) {
      setReferralCode(storedHash);
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PatientRegisterForm>({
    resolver: zodResolver(patientRegisterSchema),
    defaultValues: {
      lgpdConsent: false,
      termsConsent: false,
    },
  });

  const password = watch('password', '');
  const lgpdConsent = watch('lgpdConsent', false);
  const termsConsent = watch('termsConsent', false);

  const passwordStrength = useMemo(
    () => getPasswordStrength(password),
    [password]
  );

  const formatCpf = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const formatDate = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  };

  const onSubmit = async (data: PatientRegisterForm) => {
    setApiError(null);

    try {
      const [day, month, year] = data.birthDate.split('/');
      const birthDateIso = `${year}-${month}-${day}`;

      const res = await authApi.register({
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password,
        role: 'PATIENT',
        cpf: data.cpf,
        phone: data.phone,
        birthDate: birthDateIso,
        lgpdConsent: Boolean(data.lgpdConsent),
        termsConsent: Boolean(data.termsConsent),
        referralCode: referralCode || undefined,
        captchaToken,
      });

      const newUserId = res?.user?.id;
      if (newUserId) {
        const partnerId = getCookie('medikei_partner_id');
        const cookieId = getCookie('medikei_partner');
        if (partnerId || cookieId) {
          try {
            await httpClient.post('/tracking/referral', {
              patientId: newUserId,
              partnerId: partnerId || undefined,
              cookieId: cookieId || undefined,
              source: 'COOKIE',
            });
          } catch (err) {
            console.error('Erro ao vincular indicação por cookie:', err);
          }
        }
      }

      setRegistrationSuccess(true);
    } catch (err: any) {
      const error = err as Error;
      if (error instanceof AuthApiError) {
        setApiError(error.message);
      } else {
        setApiError(err?.message || 'Erro ao realizar cadastro de paciente. Verifique os dados e tente novamente.');
      }
    }
  };

  if (registrationSuccess) {
    return (
      <AuthLayout>
        <div className="text-center py-6 space-y-4">
          <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/40 rounded-full flex items-center justify-center mx-auto text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={32} />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Conta Criada com Sucesso!</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Seu cadastro foi realizado com sucesso. Enviamos um link de confirmação para seu e-mail.
          </p>
          <Link to={loginUrl}>
            <Button variant="primary" className="w-full rounded-xl font-bold mt-2">
              Acessar minha conta
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeader
        title="Criar Conta de Paciente"
        description="Cadastre-se na plataforma integrada de saúde digital"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {apiError && (
          <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-xl p-4 flex items-start gap-3 shadow-xs">
            <XCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-0.5">
              <p className="text-xs font-bold text-rose-800 dark:text-rose-200 uppercase tracking-wider">Atenção no cadastro</p>
              <p className="text-xs sm:text-sm text-rose-700 dark:text-rose-300 leading-normal">{apiError}</p>
            </div>
          </div>
        )}

        <Input
          label="Nome Completo"
          type="text"
          placeholder="Seu nome completo"
          autoComplete="name"
          autoFocus
          {...register('name')}
          error={errors.name?.message}
        />

        <Input
          label="Email"
          type="email"
          placeholder="seu@email.com"
          autoComplete="email"
          {...register('email')}
          error={errors.email?.message}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="CPF"
            type="text"
            placeholder="000.000.000-00"
            autoComplete="off"
            {...register('cpf')}
            onChange={(e) => {
              const formatted = formatCpf(e.target.value);
              setValue('cpf', formatted, { shouldValidate: true });
            }}
            error={errors.cpf?.message}
          />

          <Input
            label="Telefone / WhatsApp"
            type="tel"
            placeholder="(00) 00000-0000"
            autoComplete="tel"
            {...register('phone')}
            onChange={(e) => {
              const formatted = formatPhone(e.target.value);
              setValue('phone', formatted, { shouldValidate: true });
            }}
            error={errors.phone?.message}
          />
        </div>

        <Input
          label="Data de Nascimento"
          type="text"
          placeholder="DD/MM/AAAA"
          autoComplete="bday"
          {...register('birthDate')}
          onChange={(e) => {
            const formatted = formatDate(e.target.value);
            setValue('birthDate', formatted, { shouldValidate: true });
          }}
          error={errors.birthDate?.message}
        />

        <PasswordInput
          label="Senha"
          placeholder="••••••••"
          autoComplete="new-password"
          {...register('password')}
          error={errors.password?.message}
        />

        {password && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn('h-full transition-all duration-300', passwordStrength.color)}
                  style={{ width: `${passwordStrength.score}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {passwordStrength.label}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-1 text-[11px]">
              {passwordValidationRules.map((rule) => {
                const passed = rule.test(password);
                return (
                  <div
                    key={rule.id}
                    className={cn(
                      'flex items-center gap-1.5',
                      passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                    )}
                  >
                    {passed ? (
                      <CheckCircle size={12} className="text-emerald-500 shrink-0" />
                    ) : (
                      <div className="w-2.5 h-2.5 rounded-full border border-border shrink-0" />
                    )}
                    <span>{rule.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <PasswordInput
          label="Confirmar Senha"
          placeholder="••••••••"
          autoComplete="new-password"
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />

        {/* Termos e LGPD */}
        <div className="space-y-3 pt-2">
          <Checkbox
            label={
              <span className="text-xs text-muted-foreground leading-tight">
                Li e aceito a{' '}
                <Link to="/privacidade" className="text-primary hover:underline font-semibold" target="_blank">
                  Política de Privacidade
                </Link>{' '}
                e estou ciente de como meus dados serão tratados conforme a LGPD.{' '}
                <span className="text-rose-500">*</span>
              </span>
            }
            checked={lgpdConsent}
            onChange={(e) => setValue('lgpdConsent', e.target.checked, { shouldValidate: true })}
          />
          {errors.lgpdConsent && (
            <p className="text-xs text-rose-500">{errors.lgpdConsent.message}</p>
          )}

          <Checkbox
            label={
              <span className="text-xs text-muted-foreground leading-tight">
                Li e aceito os{' '}
                <Link to="/termos" className="text-primary hover:underline font-semibold" target="_blank">
                  Termos de Uso
                </Link>{' '}
                da plataforma Medikei.{' '}
                <span className="text-rose-500">*</span>
              </span>
            }
            checked={termsConsent}
            onChange={(e) => setValue('termsConsent', e.target.checked, { shouldValidate: true })}
          />
          {errors.termsConsent && (
            <p className="text-xs text-rose-500">{errors.termsConsent.message}</p>
          )}
        </div>

        <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />

        <Button
          type="submit"
          className="w-full rounded-xl font-bold shadow-md shadow-primary/20 py-3 text-sm mt-2 cursor-pointer"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting ? 'Processando cadastro...' : 'Criar Conta de Paciente'}
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground font-medium">ou cadastre-se com</span>
          </div>
        </div>

        <GoogleLoginButton mode="register" onError={(msg) => setApiError(msg)} />

        <div className="pt-2 text-center">
          <p className="text-sm text-muted-foreground">
            Já tem uma conta?{' '}
            <Link
              to={loginUrl}
              className="font-semibold text-primary hover:underline transition-colors"
            >
              Fazer login
            </Link>
          </p>
        </div>
      </form>

      <div className="mt-6 pt-4 border-t border-border flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
        <span>Dados protegidos com criptografia de ponta a ponta e sigilo médico</span>
      </div>
    </AuthLayout>
  );
}

export default RegisterPage;
